import React, { useState, useRef, useEffect } from 'react';
import { isEnterKey, isEscapeKey } from '../../../utils/keyboard';

interface InlineEditableFieldProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  multiline?: boolean;
  autoFocus?: boolean;
  maxLength?: number;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onBlur?: () => void;
  onFocus?: () => void;
}

const InlineEditableField: React.FC<InlineEditableFieldProps> = ({
  id,
  value,
  onChange,
  placeholder = '',
  className = '',
  style = {},
  multiline = false,
  autoFocus = false,
  maxLength,
  onKeyDown,
  onBlur,
  onFocus,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const editableRef = useRef<HTMLDivElement>(null);

  // Sync local value with prop value when not editing
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value);
    }
  }, [value, isEditing]);

  // Auto focus when switching to edit mode and set DOM content once
  useEffect(() => {
    if (isEditing && editableRef.current) {
      // Set DOM content without involving React children to avoid caret jumps
      editableRef.current.textContent = localValue || '';
      editableRef.current.focus();
      // Place cursor at end
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(editableRef.current);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }, [isEditing]); // intentionally not depending on localValue

  // Auto focus on mount if specified
  useEffect(() => {
    if (autoFocus) {
      setIsEditing(true);
    }
  }, [autoFocus]);

  const handleClick = () => {
    if (!isEditing) {
      setIsEditing(true);
    }
  };

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const newValue = e.currentTarget.textContent || '';

    // Check maxLength if specified
    if (maxLength && newValue.length > maxLength) {
      const truncatedValue = newValue.substring(0, maxLength);
      e.currentTarget.textContent = truncatedValue;
      setLocalValue(truncatedValue);
      onChange(truncatedValue);

      // Move cursor to end after truncation
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(e.currentTarget);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
      return;
    }

    setLocalValue(newValue);
    onChange(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isEnterKey(e) && !multiline) {
      e.preventDefault();
      handleBlur();
    }

    if (isEscapeKey(e)) {
      e.preventDefault();
      setLocalValue(value);
      handleBlur();
    }

    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (onBlur) {
      onBlur();
    }
  };

  const handleFocus = () => {
    setIsEditing(true);
    if (onFocus) {
      onFocus();
    }
  };

  const displayValue = localValue || placeholder;
  const isEmpty = !localValue || localValue.trim() === '';
  const showPlaceholder = isEmpty && !isEditing;

  return (
    <div
      id={id}
      ref={editableRef}
      contentEditable={true}
      suppressContentEditableWarning={true}
      onClick={handleClick}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onFocus={handleFocus}
      className={`outline-none cursor-text ${showPlaceholder ? 'text-gray-400' : ''} ${className}`}
      style={{
        minHeight: multiline ? '2.5rem' : '1.5rem',
        wordWrap: 'break-word',
        whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
        overflow: multiline ? 'auto' : 'hidden',
        ...style,
      }}
      data-placeholder={placeholder}
    >
      {
        !isEditing && (showPlaceholder ? placeholder : localValue)
      }
    </div>
  );
};

export default InlineEditableField;
